import { useState } from "react";
import { Alert, Button, Form, Modal, Spinner, Stack } from "react-bootstrap";
import * as yup from "yup";
import { Form as FormikForm, Formik, FormikHelpers } from "formik";
import { ToolsType } from "../../constant/ToolsType";
import { UploadBoundaryRequest } from "../../types/Boundary/UploadBoundaryRequest";
import { useBoundarySelectContext } from "./BoundarySelectContext";
import { useBoundaryService } from "../../hooks";
import { MultiPolygon } from "ol/geom";
import { BoundarySource } from "../../constant/BoundarySource";
import ThemedModal from "../../@ui/Modal/ThemedModal";
import { useAuthContext } from "../auth/AuthContext";

const schema = yup.object({
  file: yup.mixed().required(),
  name: yup.string().required(),
  saveCustomBoundary: yup.boolean().required(),
});

const UploadBoundaryModal = () => {
  const initialValues: UploadBoundaryRequest = {
    name: "",
    saveCustomBoundary: false,
  };
  const [loading, setLoading] = useState(false);
  const { user } = useAuthContext();
  const { boundaries, addBoundary, activeTool, setActiveTool } =
    useBoundarySelectContext();
  const [apiErrorMessage, setApiErrorMessage] = useState<string | undefined>(
    undefined
  );

  const boundaryService = useBoundaryService();

  const handleSubmit = (
    request: UploadBoundaryRequest,
    { resetForm }: FormikHelpers<UploadBoundaryRequest>
  ) => {
    setLoading(true);
    boundaryService
      .UploadBoundary(request)
      .then((boundary) => {
        addBoundary({
          ...boundary,
          boundaryId: request.saveCustomBoundary
            ? boundary.boundaryId
            : boundaries.filter(
                (b) =>
                  b.source === BoundarySource.DrawnBoundary ||
                  b.source === BoundarySource.UploadedBoundary
              ).length *
                -1 -
              1,
          geometry: new MultiPolygon(boundary.geometry.coordinates),
          source: request.saveCustomBoundary
            ? BoundarySource.KnownBoundary
            : BoundarySource.UploadedBoundary,
        });
        resetForm();
        setActiveTool(ToolsType.NONE);
      })
      .catch((e) => {
        setApiErrorMessage(e.response.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      validateOnMount
      validateOnChange
      validateOnBlur
      onSubmit={handleSubmit}
    >
      {({
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        setFieldValue,
        isValid,
        handleSubmit,
        resetForm,
      }) => (
        <FormikForm>
          <ThemedModal
            show={activeTool === ToolsType.UPLOAD_AREA}
            onHide={() => {
              resetForm();
              setActiveTool(ToolsType.NONE);
            }}
          >
            <Modal.Header className="bg-secondary text-white" closeButton>
              Upload Area
            </Modal.Header>
            <Modal.Body>
              <Alert variant="info">
                To upload an area, create a shapefile or geojson with the following requirements:
                <ul>
                  <li>The file's projection must be WGS84</li>
                  <li>The geometry must be a polygon</li>
                  <li>File must be shapefile/geojson</li>
                  <li>Shapefile must be zipped with all necessary files (.shp, .shx, .dbf)</li>
                  <li>GeoJSON must be a valid JSON file with extensions (.json, .geojson)</li>
                </ul>
              </Alert>
              <Stack className="gap-3">
                <Form.Group>
                  <Form.Label htmlFor="">Shapefile (zipped) & GeoJSON (json)</Form.Label>
                  <Form.Control
                    id="file"
                    name="file"
                    type="file"
                    accept=".zip,.json,.geojson"
                    onChange={(e) =>
                      setFieldValue("file", (e.currentTarget as any).files[0])
                    }
                    onBlur={handleBlur}
                    isValid={touched.file && !errors.file}
                    isInvalid={touched.file && !!errors.file}
                  />
                  {errors.file && (
                    <Form.Control.Feedback type="invalid">
                      {errors.file}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
                <Form.Group>
                  <Form.Label htmlFor="">Name</Form.Label>
                  <Form.Control
                    id="name"
                    name="name"
                    type="text"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isValid={touched.name && !errors.name}
                    isInvalid={touched.name && !!errors.name}
                  />
                  {errors.name && (
                    <Form.Control.Feedback type="invalid">
                      {errors.name}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
                {user && (
                  <Form.Group>
                    <Form.Label htmlFor="access">
                      Save for future use?
                    </Form.Label>
                    <Form.Check
                      id="saveCustomBoundary"
                      name="saveCustomBoundary"
                      type="switch"
                      checked={values.saveCustomBoundary}
                      label={values.saveCustomBoundary ? "Save" : "Don't Save"}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Form.Group>
                )}
                {apiErrorMessage && (
                  <div className="p-2 bg-danger text-white  rounded">
                    {apiErrorMessage}
                  </div>
                )}
              </Stack>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="primary"
                onClick={() => handleSubmit()}
                disabled={!isValid || loading}
              >
                Add Area {loading && <Spinner size="sm" />}
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  resetForm();
                  setActiveTool(ToolsType.NONE);
                }}
              >
                Cancel
              </Button>
            </Modal.Footer>
          </ThemedModal>
        </FormikForm>
      )}
    </Formik>
  );
};

export default UploadBoundaryModal;
